var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { resetEditorChromes } from '@sitecore-jss/sitecore-jss/utils';
import { h, ref, onMounted } from 'vue';
import { MissingComponent } from './MissingComponent';
import { HiddenRendering, HIDDEN_RENDERING_NAME } from './HiddenRendering';
export const getPlaceholderDataFromRenderingData = (rendering, name) => {
    let result;
    if (rendering && rendering.placeholders && Object.keys(rendering.placeholders).length > 0) {
        result = rendering.placeholders[name];
    }
    else {
        result = null;
    }
    if (!result) {
        console.warn(`Placeholder '${name}' was not found in the current rendering data`, JSON.stringify(rendering, null, 2));
        return [];
    }
    return result;
};
/**
 * Gets Vue elements (VNodes) from placeholder data, using componentFactory to resolve component definitions
 * and createVueElement to create VNodes for use in a Vue `render` function. Note: you can't use VNodes in
 * component templates. You'll need to convert the VNodes to "renderable" components, a.k.a. dynamic components.
 * @see convertVNodesToDynamicComponents or @see getDynamicComponentsFromRenderingData for options.
 * @param {Array<ComponentRendering | HtmlElementRendering>} placeholderData
 * @param {PlaceholderProps} placeholderProps
 * @param {ComponentFactory} [componentFactory]
 * @returns {VNode[]} vnodes
 */
export function getVNodesForRenderingData(placeholderData, placeholderProps, componentFactory) {
    const { name: placeholderName, fields: placeholderFields, params: placeholderParams, missingComponentComponent, hiddenRenderingComponent } = placeholderProps, unmappedPlaceholderProps = __rest(placeholderProps, ["name", "fields", "params", "missingComponentComponent", "hiddenRenderingComponent"]);
    return placeholderData
        .map((rendering, index) => {
        const key = rendering.uid ? rendering.uid : `component-${index}`;
        // if the element is not a 'component rendering', render it 'raw'
        if (!rendering.componentName && rendering.name) {
            return createRawElement(rendering);
        }
        let component;
        if (rendering.componentName === HIDDEN_RENDERING_NAME) {
            component = hiddenRenderingComponent || HiddenRendering;
        }
        else {
            component = getComponentForRendering(rendering, componentFactory);
        }
        if (rendering.componentName && !component) {
            console.error(`Placeholder ${placeholderName} contains unknown component ${rendering.componentName}. Ensure that a Vue component exists for it, and that it is mapped in your component factory.`);
            component = missingComponentComponent || MissingComponent;
        }
        const finalProps = Object.assign(Object.assign({}, unmappedPlaceholderProps), { rendering });
        if (placeholderFields || rendering.fields) {
            finalProps.fields = Object.assign(Object.assign({}, placeholderFields), rendering.fields);
        }
        if (placeholderParams || rendering.params) {
            finalProps.params = Object.assign(Object.assign({}, placeholderParams), rendering.params);
        }
        return h(component, Object.assign(Object.assign({}, finalProps), { key }));
    })
        .filter((element) => element); // remove nulls;
}
/**
 * Convenience method that calls {@link getVNodesForRenderingData} and {@link convertVNodesToDynamicComponents}
 * to return "renderable" components, i.e. components that can be rendered in a Vue template, a.k.a. dynamic components.
 * @param {Array<ComponentRendering | HtmlElementRendering>} placeholderData
 * @param {PlaceholderProps} placeholderProps
 * @param {ComponentFactory} componentFactory
 * @returns {JssDynamicComponent[]} dynamic components
 */
export function getDynamicComponentsFromRenderingData(placeholderData, placeholderProps, componentFactory) {
    return convertVNodesToDynamicComponents(getVNodesForRenderingData(placeholderData, placeholderProps, componentFactory));
}
/**
 * Converts VNodes to components that render the vnode.
 * Also evaluates VNodes to determine if they are Experience Editor components/elements and if so,
 * adds an identifying property to the component.
 * @param {VNode[]} vnodes
 * @returns {JssDynamicComponent[]} dynamic components
 */
export function convertVNodesToDynamicComponents(vnodes) {
    return vnodes.map((vnode) => {
        var _a, _b;
        const component = {
            $props: vnode.props,
            inheritAttrs: false,
            render() {
                return vnode;
            },
        };
        if (((_a = vnode.props.elem) === null || _a === void 0 ? void 0 : _a.name) === 'code' && ((_b = vnode.props.elem) === null || _b === void 0 ? void 0 : _b.type) === 'text/sitecore') {
            component.isxEditorComponent = true;
        }
        return component;
    });
}
/**
 * @param {any} elem
 */
function createRawElement(elem) {
    if (!elem.name) {
        console.error('"elem.name" is undefined in "createRawElement". ' +
            'Something is likely wrong with your component data. Ensure that your components have a name.');
        return null;
    }
    const component = {
        setup() {
            const elRef = ref(null);
            onMounted(() => {
                /*
                 * Since we can't set the "key" via Vue attributes
                 * so we can set in the DOM after render.
                 * onMounted is called when the initial page load is happening
                 * onMounted is not called when we add new rendering on the page,
                 * so we will replace phkey by key
                 */
                if (!Array.isArray(elem.attributes) &&
                    elem.attributes &&
                    elem.attributes.chrometype === 'placeholder' &&
                    elem.attributes.key) {
                    elRef.value.setAttribute('key', elem.attributes.key);
                    // Reset chromes since sometimes experience editor script is executed earlier
                    // than Vue script and EE can't set required attributes and chromes aren't visible
                    // Also required for Horizon
                    resetEditorChromes();
                }
            });
            return () => {
                var _a;
                return h(elem.name, Object.assign(Object.assign({}, elem.attributes), { innerHTML: elem.contents, phkey: (_a = elem.attributes) === null || _a === void 0 ? void 0 : _a.key, ref: elRef }));
            };
        },
    };
    return h(component, { elem });
}
/**
 * @param {Object} renderingDefinition
 * @param {string} renderingDefinition.componentName
 * @param {ComponentFactory} [componentFactory]
 */
function getComponentForRendering(renderingDefinition, componentFactory) {
    if (!componentFactory) {
        console.warn(`No componentFactory was available to service request for component ${renderingDefinition.componentName}`);
        return null;
    }
    const component = componentFactory(renderingDefinition.componentName);
    return component;
}
